import {ApolloError} from '@apollo/client';

import {AdcConjugationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  CONJUGATION_SITES_LOOKUP_ERROR,
  ConjugationSite,
} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteConstants';
import {conjugationSiteKey} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteUtils';
import {AntibodyDomain, fetchAntibodyDomains} from 'molbio/adcs/conjugation-sites/antibodyDomainsQuery';

export interface FindConjugationSiteCandidatesInput {
  antibodyId: string;
  conjugationMethod: AdcConjugationMethod;
}

export type ConjugationSiteCandidatesResult =
  | {supported: true; candidates: Array<ConjugationSite>}
  | {supported: false}
  /** The candidates couldn't be looked up, e.g. because the antibody's domains failed to load. */
  | {error: string};

/**
 * The IgG1 sites that engineered cysteine conjugation can use: EU C118 in CH1 (from A118C) and EU C239 in CH2
 * (from S239C). An antibody has only the ones it was engineered with.
 */
export const ENGINEERED_CYSTEINE_SITES: ReadonlyArray<ConjugationSite> = [
  {domainLabel: 'CH1(Heavy)', residueIndex: 0},
  {domainLabel: 'CH2(Heavy)', residueIndex: 8},
];

/**
 * The IgG1 sites that disulfide reduction can use: the interchain cysteines EU C220, C226, and C229 in the
 * hinge and EU C214 in CL.
 */
export const DISULFIDE_REDUCTION_SITES: ReadonlyArray<ConjugationSite> = [
  {domainLabel: 'H(Heavy)', residueIndex: 4},
  {domainLabel: 'H(Heavy)', residueIndex: 10},
  {domainLabel: 'H(Heavy)', residueIndex: 13},
  {domainLabel: 'CL(Light)', residueIndex: 106},
];

interface ConjugatedResidues {
  /** The one-letter code of the amino acid the method attaches payloads to, e.g. "C" for cysteine. */
  residue: string;
  /** The sites the method can use, if they are that residue, or null if it can use every such residue. */
  availableSites: ReadonlyArray<ConjugationSite> | null;
}

const CONJUGATED_RESIDUES_BY_METHOD: Partial<Record<AdcConjugationMethod, ConjugatedResidues>> = {
  [AdcConjugationMethod.ENGINEERED_CYSTEINE]: {residue: 'C', availableSites: ENGINEERED_CYSTEINE_SITES},
  [AdcConjugationMethod.DISULFIDE_CONJUGATION]: {residue: 'C', availableSites: DISULFIDE_REDUCTION_SITES},
  [AdcConjugationMethod.LYSINE_CONJUGATION]: {residue: 'K', availableSites: null},
};

/**
 * Finds the residues of the given amino acid in the given antibody domains, skipping domains whose sequence
 * is inaccessible.
 *
 * @param residue The one-letter code of the amino acid, e.g. "C" for cysteine
 * @param availableSites If given, only residues at these sites are found
 */
export function findResidueConjugationSites(
  domains: ReadonlyArray<AntibodyDomain>,
  residue: string,
  availableSites: ReadonlyArray<ConjugationSite> | null = null
): Array<ConjugationSite> {
  const availableKeys =
    availableSites != null ? new Set(availableSites.map((site) => conjugationSiteKey(site))) : null;
  const sites: Array<ConjugationSite> = [];
  for (const domain of domains) {
    if (domain.entity.__typename === 'InaccessibleInfoNode') {
      continue;
    }
    const residues = domain.entity.experimentalCurrentVersion.aminoAcids;
    for (let residueIndex = 0; residueIndex < residues.length; residueIndex++) {
      const site = {domainLabel: domain.label, residueIndex};
      if (
        residues[residueIndex].toUpperCase() === residue &&
        (availableKeys == null || availableKeys.has(conjugationSiteKey(site)))
      ) {
        sites.push(site);
      }
    }
  }
  return sites;
}

/**
 * Returns the residues on an antibody where a payload could attach using the given conjugation method.
 *
 * Engineered cysteine and disulfide conjugation attach payloads to cysteines, so their candidates are the
 * antibody's cysteines at the sites each method can use (ENGINEERED_CYSTEINE_SITES and
 * DISULFIDE_REDUCTION_SITES). Lysine conjugation can use every lysine. An inaccessible antibody has no
 * candidates. Every other method is reported as unsupported.
 */
export async function findConjugationSiteCandidates({
  antibodyId,
  conjugationMethod,
}: FindConjugationSiteCandidatesInput): Promise<ConjugationSiteCandidatesResult> {
  const conjugatedResidues = CONJUGATED_RESIDUES_BY_METHOD[conjugationMethod];
  if (conjugatedResidues == null) {
    return {supported: false};
  }
  let domains: Array<AntibodyDomain> | null;
  try {
    domains = await fetchAntibodyDomains(antibodyId);
  } catch (error) {
    if (error instanceof ApolloError) {
      return {error: CONJUGATION_SITES_LOOKUP_ERROR};
    }
    throw error;
  }
  const {residue, availableSites} = conjugatedResidues;
  return {
    supported: true,
    candidates: domains != null ? findResidueConjugationSites(domains, residue, availableSites) : [],
  };
}
